import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useHalls } from '../../api/queries/halls';
import { useSiteReviews, useSiteStats } from '../../api/queries/site';
import { useT } from '../../i18n';
import { cityGroups, featuredHalls, spotlightHall } from './data';
import { Hero } from './Hero';
import { Intro, shouldPlayIntro } from './Intro';
import {
  EventTypes,
  FeaturedIndex,
  Guides,
  HowItWorks,
  Locations,
  Numbers,
  OccasionsScroll,
  Reviews,
  RingCta,
  Spotlight,
  Statement,
  Why,
  numberItems,
} from './sections';
import './home.css';

/**
 * The public front door. The old home page was the search page, so a
 * bookmarked "/?q=hall&location=Kochi" is sent on to /venues with its query.
 */
export function HomePage() {
  const { search } = useLocation();
  if (search && search !== '?') return <Navigate to={`/venues${search}`} replace />;
  return <Home />;
}

/**
 * Night edition home, in the reference's order: opening intro, pinned arch
 * hero with search, then numbered sections. Every live section reads real
 * data and hides itself when there is none; the numbering follows whatever
 * is shown.
 */
function Home() {
  const { t } = useT();
  const { data: halls } = useHalls({});
  const { data: stats } = useSiteStats();
  const { data: reviews } = useSiteReviews(6);

  const list = useMemo(() => halls ?? [], [halls]);
  const featured = useMemo(() => featuredHalls(list), [list]);
  const spotlight = useMemo(() => spotlightHall(list), [list]);

  const [intro, setIntro] = useState(shouldPlayIntro);
  const [ready, setReady] = useState(false);
  const reveal = useCallback(() => setReady(true), []);
  const done = useCallback(() => setIntro(false), []);

  // Without the intro, the hero makes its entrance on the next frame.
  useEffect(() => {
    if (intro) return;
    const id = requestAnimationFrame(reveal);
    return () => cancelAnimationFrame(id);
  }, [intro, reveal]);

  // Number the sections that actually render.
  let n = 0;
  const next = () => ++n;
  const show = {
    featured: featured.length > 0,
    spotlight: Boolean(spotlight && spotlight.photos.length),
    locations: cityGroups(list).length > 0,
    reviews: Boolean(reviews?.length),
    numbers: numberItems(stats, t).length > 0,
  };

  return (
    <div className="hm">
      {intro && <Intro onReveal={reveal} onDone={done} />}
      <Hero ready={ready} halls={halls} />
      <Why n={next()} />
      {show.featured && <FeaturedIndex n={next()} halls={featured} />}
      <OccasionsScroll n={next()} halls={list} />
      <Statement />
      <HowItWorks n={next()} />
      {show.spotlight && <Spotlight n={next()} hall={spotlight} />}
      {show.locations && <Locations n={next()} halls={list} />}
      <EventTypes n={next()} halls={list} />
      <Guides n={next()} />
      {show.reviews && <Reviews n={next()} reviews={reviews} />}
      {show.numbers && <Numbers n={next()} stats={stats} />}
      <RingCta />
    </div>
  );
}
